// ─────────────────────────────────────────────────────────────────
// V2 Mock Data — WDoc Platform Milestones
//
// Each milestone has its own export so panels can import only what
// they need.  All IDs are deterministic so the UI is reproducible.
//
// ⚠️  This file is MOCK ONLY.  When the real V2 backend is wired,
//     replace v2ApiService calls with axiosInstance calls and delete
//     this file (or keep it as a fallback for offline dev).
// ─────────────────────────────────────────────────────────────────

// ─── Shared / Account context ────────────────────────────────────
export const mockAccount = {
    accountId: 'acc_7f3a1b',
    accountName: 'Acme Corp',
    plan: 'professional',
    role: 'admin',
    email: 'admin@acme.com',
    createdAt: '2025-01-15T10:00:00Z',
};

export const mockUsageSnapshot = {
    pdfsGenerated: 1847,
    pdfsLimit: 5000,
    bulkJobsRun: 23,
    apiCallsThisMonth: 12450,
    apiCallsLimit: 50000,
    storageUsedMb: 320,
    storageLimitMb: 1024,
    period: '2026-05',
};

// ─── M4  OpenAPI + Docs Content ──────────────────────────────────
export const mockApiEndpoints = [
    {
        method: 'GET',
        path: '/api/v2/templates',
        summary: 'List templates',
        auth: 'JWT / API-Key',
        scope: 'templates.read',
        status: 'stable',
    },
    {
        method: 'POST',
        path: '/api/v2/templates',
        summary: 'Create template',
        auth: 'JWT / API-Key',
        scope: 'templates.write',
        status: 'stable',
    },
    {
        method: 'GET',
        path: '/api/v2/templates/{templateId}',
        summary: 'Get template by ID',
        auth: 'JWT / API-Key',
        scope: 'templates.read',
        status: 'stable',
    },
    {
        method: 'GET',
        path: '/api/v2/templates/{templateId}/schema',
        summary: 'Get template JSON schema',
        auth: 'JWT / API-Key',
        scope: 'templates.read',
        status: 'stable',
    },
    {
        method: 'PUT',
        path: '/api/v2/templates/{templateId}',
        summary: 'Update template',
        auth: 'JWT / API-Key',
        scope: 'templates.write',
        status: 'stable',
    },
    {
        method: 'DELETE',
        path: '/api/v2/templates/{templateId}',
        summary: 'Delete template',
        auth: 'JWT / API-Key',
        scope: 'templates.write',
        status: 'stable',
    },
    {
        method: 'POST',
        path: '/api/v2/pdf/generate',
        summary: 'Generate single PDF',
        auth: 'JWT / API-Key',
        scope: 'pdf.generate',
        status: 'stable',
    },
    {
        method: 'POST',
        path: '/api/v2/pdf/generate-bulk-async',
        summary: 'Start async bulk PDF job',
        auth: 'JWT / API-Key',
        scope: 'pdf.generate',
        status: 'stable',
    },
    {
        method: 'GET',
        path: '/api/v2/pdf/jobs/{jobId}',
        summary: 'Get bulk job status',
        auth: 'JWT / API-Key',
        scope: 'pdf.read',
        status: 'stable',
    },
    {
        method: 'GET',
        path: '/api/v2/pdfs/{pdfId}/download',
        summary: 'Download generated PDF',
        auth: 'JWT / API-Key',
        scope: 'pdf.read',
        status: 'stable',
    },
    {
        method: 'POST',
        path: '/api/v2/api-keys',
        summary: 'Create API key',
        auth: 'JWT',
        scope: 'api-keys.manage',
        status: 'stable',
    },
    {
        method: 'GET',
        path: '/api/v2/api-keys',
        summary: 'List API keys',
        auth: 'JWT',
        scope: 'api-keys.manage',
        status: 'stable',
    },
    {
        method: 'DELETE',
        path: '/api/v2/api-keys/{keyId}',
        summary: 'Revoke API key',
        auth: 'JWT',
        scope: 'api-keys.manage',
        status: 'stable',
    },
    {
        method: 'POST',
        path: '/api/v2/webhooks',
        summary: 'Create webhook endpoint',
        auth: 'JWT / API-Key',
        scope: 'webhooks.manage',
        status: 'stable',
    },
    {
        method: 'GET',
        path: '/api/v2/webhooks',
        summary: 'List webhook endpoints',
        auth: 'JWT / API-Key',
        scope: 'webhooks.manage',
        status: 'stable',
    },
    {
        method: 'POST',
        path: '/api/v2/webhooks/{id}/test',
        summary: 'Test webhook endpoint',
        auth: 'JWT / API-Key',
        scope: 'webhooks.manage',
        status: 'stable',
    },
    {
        method: 'GET',
        path: '/api/v2/plans',
        summary: 'List public plans',
        auth: 'none',
        scope: '-',
        status: 'stable',
    },
    {
        method: 'GET',
        path: '/api/v2/plans/me',
        summary: 'Get my plan & usage',
        auth: 'JWT',
        scope: 'plans.read',
        status: 'stable',
    },
    {
        method: 'GET',
        path: '/api/v2/templates/migrate/designer-documents',
        summary: 'List migratable designer docs',
        auth: 'JWT',
        scope: 'templates.read',
        status: 'beta',
    },
    {
        method: 'POST',
        path: '/api/v2/templates/migrate/from-designer',
        summary: 'Migrate designer doc to template',
        auth: 'JWT',
        scope: 'templates.write',
        status: 'beta',
    },
];

// ─── M5  Versioning + Library + Search ───────────────────────────
export const mockTemplateVersions = [
    {
        templateId: 'tpl_inv_001',
        name: 'Invoice Template',
        versions: [
            { version: 3, createdAt: '2026-04-28T14:30:00Z', author: 'admin@acme.com', changeNote: 'Added payment terms section' },
            { version: 2, createdAt: '2026-03-15T09:00:00Z', author: 'admin@acme.com', changeNote: 'Updated logo and colors' },
            { version: 1, createdAt: '2026-01-20T11:00:00Z', author: 'admin@acme.com', changeNote: 'Initial version' },
        ],
    },
    {
        templateId: 'tpl_stmt_002',
        name: 'Bank Statement',
        versions: [
            { version: 2, createdAt: '2026-04-10T16:00:00Z', author: 'finance@acme.com', changeNote: 'Added currency column' },
            { version: 1, createdAt: '2026-02-05T08:30:00Z', author: 'finance@acme.com', changeNote: 'Initial version' },
        ],
    },
];

export const mockLibrarySearchResults = [
    { id: 'tpl_inv_001', name: 'Invoice Template', category: 'finance', tags: ['invoice', 'billing'], version: 3 },
    { id: 'tpl_stmt_002', name: 'Bank Statement', category: 'finance', tags: ['statement', 'banking'], version: 2 },
    { id: 'tpl_cert_003', name: 'Certificate of Completion', category: 'education', tags: ['certificate', 'award'], version: 1 },
    { id: 'tpl_contract_004', name: 'Service Contract', category: 'legal', tags: ['contract', 'agreement'], version: 1 },
    { id: 'tpl_payslip_005', name: 'Payslip', category: 'hr', tags: ['payslip', 'salary', 'bordro'], version: 2 },
];

// ─── M6  SDKs (JS/TS + Python) ───────────────────────────────────
export const mockSdkInfo = {
    javascript: {
        packageName: '@wdoc/sdk',
        version: '1.2.0',
        installCmd: 'npm install @wdoc/sdk',
        repoUrl: 'https://github.com/wdoc/wdoc-sdk-js',
        codeExample: `import { WDocClient } from '@wdoc/sdk';

const client = new WDocClient({
  apiKey: 'wdoc_live_abc123...',
  baseUrl: 'https://api.wdoc.io'
});

// Generate a PDF from a template
const pdf = await client.pdfs.generate({
  templateId: 'tpl_inv_001',
  data: {
    customerName: 'Acme Corp',
    amount: 1500.00,
    dueDate: '2026-06-01'
  }
});

console.log('PDF URL:', pdf.downloadUrl);`,
    },
    python: {
        packageName: 'wdoc-sdk',
        version: '1.1.0',
        installCmd: 'pip install wdoc-sdk',
        repoUrl: 'https://github.com/wdoc/wdoc-sdk-python',
        codeExample: `from wdoc_sdk import WDocClient

client = WDocClient(
    api_key="wdoc_live_abc123...",
    base_url="https://api.wdoc.io"
)

# Generate a PDF from a template
pdf = client.pdfs.generate(
    template_id="tpl_inv_001",
    data={
        "customerName": "Acme Corp",
        "amount": 1500.00,
        "dueDate": "2026-06-01"
    }
)

print(f"PDF URL: {pdf.download_url}")`,
    },
};

// ─── M7  Signed PDF + Cert Management ───────────────────────────
export const mockCertificates = [
    {
        id: 'cert_001',
        name: 'Acme Signing Cert',
        issuer: 'WDoc Internal CA',
        subject: 'CN=Acme Corp, O=Acme Inc, C=TR',
        validFrom: '2025-06-01T00:00:00Z',
        validTo: '2027-06-01T00:00:00Z',
        serialNumber: '4A:3B:2C:1D',
        status: 'active',
        algorithm: 'SHA256withRSA',
        keySize: 2048,
    },
    {
        id: 'cert_002',
        name: 'Acme Expired Cert',
        issuer: 'WDoc Internal CA',
        subject: 'CN=Acme Corp Old, O=Acme Inc, C=TR',
        validFrom: '2023-01-01T00:00:00Z',
        validTo: '2024-01-01T00:00:00Z',
        serialNumber: '1A:2B:3C:4D',
        status: 'expired',
        algorithm: 'SHA256withRSA',
        keySize: 2048,
    },
];

export const mockSignedPdfs = [
    {
        id: 'spdf_001',
        templateId: 'tpl_inv_001',
        templateName: 'Invoice Template',
        certId: 'cert_001',
        certName: 'Acme Signing Cert',
        generatedAt: '2026-05-08T13:45:00Z',
        signatureStatus: 'valid',
        downloadUrl: '#mock-download-signed-001',
    },
    {
        id: 'spdf_002',
        templateId: 'tpl_contract_004',
        templateName: 'Service Contract',
        certId: 'cert_001',
        certName: 'Acme Signing Cert',
        generatedAt: '2026-05-07T10:20:00Z',
        signatureStatus: 'valid',
        downloadUrl: '#mock-download-signed-002',
    },
];

// ─── M8  Audit Log + Template Versioning ────────────────────────
export const mockAuditLog = [
    { id: 'log_001', timestamp: '2026-05-10T18:30:00Z', actor: 'admin@acme.com', action: 'template.update', resource: 'tpl_inv_001', detail: 'Updated Invoice Template to v3', severity: 'info' },
    { id: 'log_002', timestamp: '2026-05-10T17:15:00Z', actor: 'finance@acme.com', action: 'pdf.generate', resource: 'tpl_stmt_002', detail: 'Generated Bank Statement PDF', severity: 'info' },
    { id: 'log_003', timestamp: '2026-05-10T16:00:00Z', actor: 'admin@acme.com', action: 'apikey.create', resource: 'key_abc123', detail: 'Created API key "Production Key"', severity: 'info' },
    { id: 'log_004', timestamp: '2026-05-10T14:45:00Z', actor: 'admin@acme.com', action: 'webhook.create', resource: 'wh_003', detail: 'Created webhook endpoint https://hooks.acme.com/wdoc', severity: 'info' },
    { id: 'log_005', timestamp: '2026-05-10T12:30:00Z', actor: 'system', action: 'bulkjob.complete', resource: 'job_bulk_015', detail: 'Bulk job completed: 500/500 PDFs generated', severity: 'info' },
    { id: 'log_006', timestamp: '2026-05-09T22:10:00Z', actor: 'admin@acme.com', action: 'cert.upload', resource: 'cert_001', detail: 'Uploaded signing certificate "Acme Signing Cert"', severity: 'warning' },
    { id: 'log_007', timestamp: '2026-05-09T20:00:00Z', actor: 'finance@acme.com', action: 'template.migrate', resource: 'tpl_stmt_002', detail: 'Migrated designer doc to template', severity: 'info' },
    { id: 'log_008', timestamp: '2026-05-09T18:00:00Z', actor: 'system', action: 'auth.failed', resource: '-', detail: 'Failed login attempt for unknown@external.com', severity: 'warning' },
    { id: 'log_009', timestamp: '2026-05-09T15:30:00Z', actor: 'admin@acme.com', action: 'plan.change', resource: 'acc_7f3a1b', detail: 'Plan changed from starter to professional', severity: 'info' },
    { id: 'log_010', timestamp: '2026-05-09T10:00:00Z', actor: 'system', action: 'backup.complete', resource: 'acc_7f3a1b', detail: 'Daily backup completed (320 MB)', severity: 'info' },
];

// ─── M8.5  Storage Architecture + Backup ─────────────────────────
export const mockStorageInfo = {
    provider: 'local',
    totalUsedMb: 320,
    totalLimitMb: 1024,
    breakdown: [
        { type: 'Generated PDFs', sizeMb: 180, count: 1847 },
        { type: 'Bulk Job Archives', sizeMb: 85, count: 23 },
        { type: 'Template Assets', sizeMb: 35, count: 12 },
        { type: 'Certificates', sizeMb: 2, count: 2 },
        { type: 'Other', sizeMb: 18, count: '-' },
    ],
    backups: [
        { id: 'bak_001', timestamp: '2026-05-10T03:00:00Z', sizeMb: 310, status: 'completed', type: 'daily' },
        { id: 'bak_002', timestamp: '2026-05-09T03:00:00Z', sizeMb: 308, status: 'completed', type: 'daily' },
        { id: 'bak_003', timestamp: '2026-05-08T03:00:00Z', sizeMb: 305, status: 'completed', type: 'daily' },
        { id: 'bak_004', timestamp: '2026-05-05T03:00:00Z', sizeMb: 300, status: 'completed', type: 'weekly' },
        { id: 'bak_005', timestamp: '2026-05-01T03:00:00Z', sizeMb: 295, status: 'completed', type: 'weekly' },
    ],
    storageConfig: {
        providerLabel: 'Local Filesystem',
        basePath: '/public/storage',
        encryptionEnabled: false,
        compressionEnabled: true,
        retentionDays: 90,
    },
};

// ─── M8.7  Email + Invitation Flow ───────────────────────────────
export const mockInvitations = [
    {
        id: 'inv_001',
        email: 'ayse@acme.com',
        role: 'member',
        invitedBy: 'admin@acme.com',
        invitedAt: '2026-05-10T10:00:00Z',
        status: 'accepted',
        acceptedAt: '2026-05-10T11:30:00Z',
    },
    {
        id: 'inv_002',
        email: 'mehmet@acme.com',
        role: 'member',
        invitedBy: 'admin@acme.com',
        invitedAt: '2026-05-09T14:00:00Z',
        status: 'pending',
        acceptedAt: null,
    },
    {
        id: 'inv_003',
        email: 'fatma@partner.com',
        role: 'viewer',
        invitedBy: 'admin@acme.com',
        invitedAt: '2026-05-08T09:00:00Z',
        status: 'expired',
        acceptedAt: null,
    },
];

export const mockTeamMembers = [
    { email: 'admin@acme.com', role: 'admin', joinedAt: '2025-01-15T10:00:00Z', status: 'active' },
    { email: 'ayse@acme.com', role: 'member', joinedAt: '2026-05-10T11:30:00Z', status: 'active' },
    { email: 'finance@acme.com', role: 'member', joinedAt: '2026-02-01T08:00:00Z', status: 'active' },
    { email: 'mehmet@acme.com', role: 'member', joinedAt: null, status: 'invited' },
];

// ─── Plans (shared across M4/M5) ────────────────────────────────
export const mockPlans = [
    {
        id: 'plan_starter',
        name: 'Starter',
        priceMonthly: 0,
        pdfsPerMonth: 100,
        bulkJobsPerMonth: 0,
        apiCallsPerMonth: 1000,
        storageMb: 100,
        features: ['Single PDF generation', '5 templates', 'Email support'],
    },
    {
        id: 'plan_professional',
        name: 'Professional',
        priceMonthly: 49,
        pdfsPerMonth: 5000,
        bulkJobsPerMonth: 50,
        apiCallsPerMonth: 50000,
        storageMb: 1024,
        features: ['Bulk PDF generation', 'Unlimited templates', 'API keys', 'Webhooks', 'Signed PDFs', 'Priority support'],
    },
    {
        id: 'plan_enterprise',
        name: 'Enterprise',
        priceMonthly: null,
        pdfsPerMonth: null,
        bulkJobsPerMonth: null,
        apiCallsPerMonth: null,
        storageMb: null,
        features: ['Unlimited everything', 'Custom integrations', 'On-prem deployment', 'Dedicated support', 'SLA', 'Audit log export'],
    },
];

// ─── API Keys (shared across M4/M5) ─────────────────────────────
export const mockApiKeys = [
    {
        id: 'key_abc123',
        name: 'Production Key',
        prefix: 'wdoc_live_abc',
        scopes: ['templates.read', 'pdf.generate', 'pdf.read'],
        createdAt: '2026-03-01T08:00:00Z',
        lastUsedAt: '2026-05-10T18:00:00Z',
        status: 'active',
    },
    {
        id: 'key_def456',
        name: 'Staging Key',
        prefix: 'wdoc_test_def',
        scopes: ['templates.read', 'templates.write', 'pdf.generate', 'pdf.read', 'webhooks.manage'],
        createdAt: '2026-04-15T12:00:00Z',
        lastUsedAt: '2026-05-09T15:00:00Z',
        status: 'active',
    },
];

// ─── Webhooks (shared across M4/M5) ─────────────────────────────
export const mockWebhooks = [
    {
        id: 'wh_001',
        url: 'https://hooks.acme.com/wdoc',
        events: ['bulkjob.completed', 'pdf.generated'],
        secret: 'whsec_••••••••••••',
        createdAt: '2026-04-01T09:00:00Z',
        lastDeliveryAt: '2026-05-10T12:30:00Z',
        lastDeliveryStatus: 'success',
        status: 'active',
    },
    {
        id: 'wh_002',
        url: 'https://api.partner.io/wdoc-events',
        events: ['pdf.generated'],
        secret: 'whsec_••••••••••••',
        createdAt: '2026-05-01T14:00:00Z',
        lastDeliveryAt: '2026-05-09T20:00:00Z',
        lastDeliveryStatus: 'failed',
        status: 'active',
    },
];

// ─── Bulk Jobs (shared across M4/M7) ────────────────────────────
export const mockBulkJobs = [
    {
        id: 'job_bulk_015',
        templateId: 'tpl_inv_001',
        templateName: 'Invoice Template',
        totalRecords: 500,
        processedRecords: 500,
        failedRecords: 0,
        status: 'completed',
        createdAt: '2026-05-10T12:00:00Z',
        completedAt: '2026-05-10T12:30:00Z',
        downloadUrl: '#mock-download-bulk-015',
    },
    {
        id: 'job_bulk_016',
        templateId: 'tpl_payslip_005',
        templateName: 'Payslip',
        totalRecords: 200,
        processedRecords: 87,
        failedRecords: 2,
        status: 'processing',
        createdAt: '2026-05-10T18:00:00Z',
        completedAt: null,
        downloadUrl: null,
    },
    {
        id: 'job_bulk_014',
        templateId: 'tpl_cert_003',
        templateName: 'Certificate of Completion',
        totalRecords: 50,
        processedRecords: 50,
        failedRecords: 0,
        status: 'completed',
        createdAt: '2026-05-09T09:00:00Z',
        completedAt: '2026-05-09T09:05:00Z',
        downloadUrl: '#mock-download-bulk-014',
    },
];
